/* IntelApps — Service Section */
import React from 'react';
import PropTypes from 'prop-types';
import { useI18n } from '../i18n.js';

function ServiceIcon({ type }) {
  switch (type) {
    case 'web':
      return (
        <svg
          className="service-icon-svg"
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="3" y="4" width="18" height="14" rx="2" />
          <path d="M8 20h8" />
          <path d="M12 18v2" />
        </svg>
      );
    case 'mobile':
      return (
        <svg
          className="service-icon-svg"
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="8" y="2.5" width="8" height="19" rx="2" />
          <path d="M11 18.5h2" />
        </svg>
      );
    case 'design':
      return (
        <svg
          className="service-icon-svg"
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12 20l9-9" />
          <path d="M16.5 6.5l1 1" />
          <path d="M14 4l6 6" />
          <path d="M6 18l-2 2" />
          <path d="M3 21l3-3" />
        </svg>
      );
    case 'ai':
      return (
        <svg
          className="service-icon-svg"
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="6" y="6" width="12" height="12" rx="1.5" />
          <path d="M9 6V4M15 6V4M9 18v2M15 18v2M6 9H4M6 15H4M18 9h2M18 15h2" />
          <path d="M10 10h4v4h-4z" />
        </svg>
      );
    case 'blockchain':
      return (
        <svg
          className="service-icon-svg"
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12 2l8 4v12l-8 4-8-4V6l8-4z" />
          <path d="M12 2v20" />
          <path d="M4 6l8 4 8-4" />
          <path d="M4 18l8-4 8 4" />
        </svg>
      );
    case 'seo':
      return (
        <svg
          className="service-icon-svg"
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
          <path d="M8.5 11h5" />
          <path d="M11 8.5v5" />
        </svg>
      );
    default:
      return (
        <svg
          className="service-icon-svg"
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12 2l3 7 7 3-7 3-3 7-3-7-7-3 7-3z" />
        </svg>
      );
  }
}

ServiceIcon.propTypes = {
  type: PropTypes.string.isRequired,
};

export default function ServicesGrid({ showTeaser = false }) {
  const { t } = useI18n();

  const services = [
    { num: '01', key: 'ai', stack: ['Python', 'PyTorch', 'LLMs & agents', 'RAG', 'MLOps'] },
    { num: '02', key: 'web', stack: ['React', 'Next.js', 'Node.js', 'MongoDB', 'AWS'] },
    { num: '03', key: 'mobile', stack: ['React Native', 'Flutter', 'Swift', 'Kotlin', 'Firebase'] },
    { num: '04', key: 'design', stack: ['Figma', 'Adobe XD', 'Sketch', 'Principle', 'InVision'] },
    { num: '05', key: 'blockchain', stack: ['Solidity', 'EVM', 'Smart contracts', 'Audits', 'Indexers'] },
    { num: '06', key: 'seo', stack: ['Technical SEO', 'On-page SEO', 'Link Building', 'Analytics', 'Content Strategy'] },
  ];

  return (
    <section className="section" id="services" style={{ paddingTop: 0 }}>
      <div className="container">
        <div className="services-grid">
          {services.map((s) => (
            <div key={s.num} className="service" style={{ minHeight: showTeaser ? 'auto' : undefined }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div className="service-icon" aria-hidden="true">
                  <ServiceIcon type={s.key} />
                </div>
                <span className="service-num">{s.num}</span>
              </div>
              <h3>{t.services.items[s.key].title}</h3>
              <p className="service-desc">{t.services.items[s.key].desc}</p>
              {!showTeaser && (
                <>
                  <div className="service-stack">{s.stack.map((tech) => <span key={tech} className="tag">{tech}</span>)}</div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

ServicesGrid.propTypes = {
  // eslint-disable-next-line react/require-default-props
  showTeaser: PropTypes.bool,
};
